/**
 * PlayerTileStack.tsx
 * 
 * Vertical scrolling tile list for mobile-first player selection.
 * Replaces horizontal PlayerDeck carousel for better mobile UX.
 * 
 * PRESERVES:
 * - Same Player interface and props as PlayerDeck
 * - Same onSelectPlayer callback signature
 * - Same data display (team, name, matchup, defense rank, season avg, line, edge)
 * - Same "SELECT PLAYER →" button text and flow
 * 
 * CHANGES:
 * - Horizontal swipe → Vertical scroll
 * - Giant cards (1/screen) → Compact tiles (4-5 visible)
 * - Tap tile to expand → shows full card with SELECT PLAYER button
 */

import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Player } from './PlayerCard';

// Team color mapping for badges
const TEAM_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  // AFC East
  BUF: { bg: 'bg-blue-700', text: 'text-white', border: 'border-blue-600' },
  MIA: { bg: 'bg-teal-500', text: 'text-white', border: 'border-teal-400' },
  NE: { bg: 'bg-blue-900', text: 'text-red-400', border: 'border-blue-700' },
  NYJ: { bg: 'bg-green-700', text: 'text-white', border: 'border-green-600' },
  // AFC North
  BAL: { bg: 'bg-purple-700', text: 'text-yellow-400', border: 'border-purple-600' },
  CIN: { bg: 'bg-orange-500', text: 'text-black', border: 'border-orange-400' },
  CLE: { bg: 'bg-orange-700', text: 'text-orange-200', border: 'border-orange-600' },
  PIT: { bg: 'bg-yellow-500', text: 'text-black', border: 'border-yellow-400' },
  // AFC South
  HOU: { bg: 'bg-red-700', text: 'text-white', border: 'border-red-600' },
  IND: { bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-500' },
  JAX: { bg: 'bg-teal-600', text: 'text-yellow-400', border: 'border-teal-500' },
  TEN: { bg: 'bg-blue-400', text: 'text-red-600', border: 'border-blue-300' },
  // AFC West
  DEN: { bg: 'bg-orange-600', text: 'text-white', border: 'border-orange-500' },
  KC: { bg: 'bg-red-600', text: 'text-yellow-400', border: 'border-red-500' },
  LV: { bg: 'bg-slate-800', text: 'text-slate-200', border: 'border-slate-600' },
  LAC: { bg: 'bg-blue-500', text: 'text-yellow-400', border: 'border-blue-400' },
  // NFC East
  DAL: { bg: 'bg-blue-800', text: 'text-slate-200', border: 'border-blue-700' },
  NYG: { bg: 'bg-blue-700', text: 'text-red-500', border: 'border-blue-600' },
  PHI: { bg: 'bg-green-600', text: 'text-white', border: 'border-green-500' },
  WAS: { bg: 'bg-red-800', text: 'text-yellow-500', border: 'border-red-700' },
  // NFC North
  CHI: { bg: 'bg-orange-600', text: 'text-blue-900', border: 'border-orange-500' },
  DET: { bg: 'bg-blue-500', text: 'text-slate-200', border: 'border-blue-400' },
  GB: { bg: 'bg-green-700', text: 'text-yellow-400', border: 'border-green-600' },
  MIN: { bg: 'bg-purple-600', text: 'text-yellow-400', border: 'border-purple-500' },
  // NFC South
  ATL: { bg: 'bg-red-600', text: 'text-black', border: 'border-red-500' },
  CAR: { bg: 'bg-blue-400', text: 'text-black', border: 'border-blue-300' },
  NO: { bg: 'bg-yellow-500', text: 'text-black', border: 'border-yellow-400' },
  TB: { bg: 'bg-red-700', text: 'text-slate-200', border: 'border-red-600' },
  // NFC West
  ARI: { bg: 'bg-red-700', text: 'text-white', border: 'border-red-600' },
  LAR: { bg: 'bg-blue-600', text: 'text-yellow-400', border: 'border-blue-500' },
  LA: { bg: 'bg-blue-600', text: 'text-yellow-400', border: 'border-blue-500' },
  SF: { bg: 'bg-red-600', text: 'text-yellow-400', border: 'border-red-500' },
  SEA: { bg: 'bg-green-600', text: 'text-slate-200', border: 'border-green-500' },
};

const DEFAULT_TEAM_STYLE = { bg: 'bg-slate-700', text: 'text-slate-200', border: 'border-slate-600' };

interface PlayerTileStackProps {
  players: Player[];
  position: string;
  rivalsByPlayer: Record<number, string[]>;
  onSelectPlayer: (player: Player) => void;
}

export const PlayerTileStack: React.FC<PlayerTileStackProps> = ({
  players,
  position,
  rivalsByPlayer: _rivalsByPlayer,
  onSelectPlayer,
}) => {
  const [expandedPlayerId, setExpandedPlayerId] = useState<number | null>(null);

  if (players.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-slate-500 text-sm">No players available for {position}</p>
      </div>
    );
  }

  return (
    <div className="px-4">
      {/* Player count */}
      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider px-1 mb-3">
        {players.length} players available
      </p>

      {/* Vertical Tile Stack */}
      <div className="space-y-2 pb-4">
        {players.map((player) => {
          // Get first prop for edge calculation
          const mainProp = player.props?.[0];
          // Cast to any to access edge fields that may exist at runtime
          const propAny = mainProp as any;
          const edge = propAny?.edge ?? propAny?.edgeValue ?? 0;
          const isTough = edge < 0;
          const isFavorable = edge >= 5;
          const isExpanded = expandedPlayerId === player.id;
          const teamStyle = TEAM_COLORS[player.team] || DEFAULT_TEAM_STYLE;

          // Get defense info from prop or player (cast to any for runtime fields)
          const defRank = propAny?.defRank ?? propAny?.opponentDefenseRank ?? 16;
          const defLabel = propAny?.defLabel || getDefenseLabel(mainProp?.statType || '');

          return (
            <div
              key={player.id}
              className={`relative bg-slate-900 rounded-xl border overflow-hidden transition-all ${
                isExpanded ? 'border-indigo-500' : 'border-slate-800'
              }`}
            >
              {/* Left Color Strip - team color */}
              <div
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{ backgroundColor: player.teamColor || '#64748B' }}
              />

              {/* Tile Header - Tap to expand */}
              <button
                onClick={() => setExpandedPlayerId(isExpanded ? null : player.id)}
                className="w-full p-3 pl-4 text-left"
              >
                <div className="flex items-center gap-3">
                  {/* Team Badge */}
                  <div
                    className={`w-10 h-10 rounded-lg ${teamStyle.bg} border ${teamStyle.border} flex items-center justify-center flex-shrink-0`}
                  >
                    <span className={`text-xs font-black ${teamStyle.text}`}>
                      {player.team}
                    </span>
                  </div>

                  {/* Player Name + Team/Position on same line, Matchup below */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-white text-base truncate">{player.name}</h3>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {player.team} • {player.position}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      vs {player.opponent} • {defLabel}: #{defRank}
                    </p>
                  </div>

                  {/* Edge Badge + Value */}
                  <div className="text-right flex-shrink-0">
                    {isFavorable && (
                      <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-black px-2 py-1 rounded">
                        FAVORABLE
                      </span>
                    )}
                    {isTough && (
                      <span className="bg-red-500/20 text-red-400 text-[10px] font-black px-2 py-1 rounded">
                        TOUGH
                      </span>
                    )}
                    <p
                      className={`text-base font-mono font-bold mt-0.5 ${
                        isTough ? 'text-red-400' : isFavorable ? 'text-emerald-400' : 'text-slate-400'
                      }`}
                    >
                      {edge > 0 ? '+' : ''}
                      {typeof edge === 'number' ? edge.toFixed(1) : '—'}
                    </p>
                  </div>
                </div>
              </button>

              {/* Expanded Card Content - Same data as current PlayerCard */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-slate-800">
                  {/* VS Opponent + Season Avg Row */}
                  <div className="flex gap-3 mt-3 mb-3">
                    <div className="flex-1 bg-slate-800/50 rounded-lg p-2">
                      <p className="text-[9px] text-slate-500 font-bold uppercase">
                        VS {player.opponent}
                      </p>
                      <p className="text-xs text-slate-300">
                        {defLabel}: <span className="font-bold">#{defRank}</span>
                      </p>
                    </div>
                    <div className="flex-1 bg-slate-800/50 rounded-lg p-2">
                      <p className="text-[9px] text-slate-500 font-bold uppercase">Season AVG</p>
                      <p className="text-lg font-bold text-white">
                        {player.seasonAvg?.toFixed(1) ?? mainProp?.seasonAvg?.toFixed(1) ?? '—'}
                        <span className="text-xs text-slate-500 ml-1">
                          {getStatUnit(mainProp?.statType)}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Target Line */}
                  {mainProp && (
                    <div className="bg-slate-800/50 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[9px] text-slate-500 font-bold uppercase">Target Line</p>
                          <p className="text-2xl font-bold text-white">
                            {mainProp.line}
                            <span className="text-sm text-slate-400 ml-2">{mainProp.statType}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          {isFavorable && (
                            <p className="text-[10px] font-bold text-emerald-400 uppercase">Favorable</p>
                          )}
                          {isTough && (
                            <p className="text-[10px] font-bold text-red-400 uppercase">Tough</p>
                          )}
                          <p
                            className={`text-xl font-mono font-bold ${
                              isTough
                                ? 'text-red-400'
                                : isFavorable
                                ? 'text-emerald-400'
                                : 'text-slate-400'
                            }`}
                          >
                            {edge > 0 ? '+' : ''}
                            {typeof edge === 'number' ? edge.toFixed(1) : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SELECT PLAYER Button - EXACT same text as current */}
                  <button
                    onClick={() => onSelectPlayer(player)}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-sm py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
                  >
                    SELECT PLAYER <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Get defense label based on stat type
 */
function getDefenseLabel(statType: string): string {
  if (!statType) return 'Def';
  const lower = statType.toLowerCase();
  if (lower.includes('pass') || lower.includes('completion')) return 'Pass Def';
  if (lower.includes('rush') || lower.includes('carry') || lower.includes('carries')) return 'Rush Def';
  if (lower.includes('rec') || lower.includes('target') || lower.includes('reception')) return 'Rec Def';
  return 'Def';
}

/**
 * Get stat unit label
 */
function getStatUnit(statType?: string): string {
  if (!statType) return '';
  const lower = statType.toLowerCase();
  if (lower.includes('yard')) return 'Yds';
  if (lower.includes('reception') || lower.includes('rec')) return 'Rec';
  if (lower.includes('carry') || lower.includes('carries')) return 'Att';
  if (lower.includes('td') || lower.includes('touchdown')) return 'TDs';
  return '';
}

export default PlayerTileStack;
